import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks';
import {
  getPendingTransfers,
  approveTransfer,
  getTransferHistory,
} from '@/services/credit-worker.service';
import { PARTNER_ID } from '@/constants';

const QUERY_KEYS = {
  pendingTransfer: ['transfers', 'pending', PARTNER_ID],
  transferHistory: ['transfers', 'history', PARTNER_ID],
} as const;

export function useTransfers() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null);

  const pendingTransferQuery = useQuery({
    queryKey: QUERY_KEYS.pendingTransfer,
    queryFn: () => getPendingTransfers(PARTNER_ID),
  });

  const transferHistoryQuery = useQuery({
    queryKey: QUERY_KEYS.transferHistory,
    queryFn: () => getTransferHistory(PARTNER_ID),
  });

  // ─── Mutation ────────────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: ({ transferId, bankReference }: { transferId: string; bankReference: string }) =>
      approveTransfer(transferId, PARTNER_ID, bankReference),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingTransfer });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.transferHistory });
      toast.success('Transferencia aprobada exitosamente');
      setIsApproveModalOpen(false);
      setSelectedTransferId(null);
    },
    onError: () => {
      toast.error('Error al aprobar la transferencia. Intenta de nuevo.');
    },
  });

  // ─── Modal actions ───────────────────────────────────────────────────────

  function openApproveModal(transferId: string) {
    setSelectedTransferId(transferId);
    setIsApproveModalOpen(true);
  }

  function closeApproveModal() {
    setIsApproveModalOpen(false);
    setSelectedTransferId(null);
  }

  function confirmApprove(bankReference: string) {
    if (selectedTransferId) {
      approveMutation.mutate({ transferId: selectedTransferId, bankReference });
    }
  }

  // ─── Aggregated state ──────────────────────────────────────────────────

  const isLoading = pendingTransferQuery.isLoading || transferHistoryQuery.isLoading;
  const isError = pendingTransferQuery.isError || transferHistoryQuery.isError;
  const error = pendingTransferQuery.error ?? transferHistoryQuery.error;

  function refetchAll() {
    void pendingTransferQuery.refetch();
    void transferHistoryQuery.refetch();
  }

  return {
    // Data
    pendingTransfers: pendingTransferQuery.data?.data ?? [],
    transferHistory: transferHistoryQuery.data?.data ?? [],

    // Approve modal
    isApproveModalOpen,
    selectedTransferId,
    openApproveModal,
    closeApproveModal,
    confirmApprove,
    isApproving: approveMutation.isPending,

    // Aggregated state
    isLoading,
    isError,
    error,
    refetchAll,
  };
}
